import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpServiceBase } from '../classes/HttpServiceBase';
import { Equipment } from './equipment.service';

/**
 * What the add/edit form submits.
 *
 * `image` is not sent — the server owns it. A new photo goes up as base64 and
 * the server stores the S3 key; omitting the image fields entirely means "leave
 * the current photo alone", and `removeImage` clears it.
 */
export interface EquipmentPayload extends Omit<Equipment, 'id' | 'image'> {
    imageBase64?: string;
    imageFilename?: string;
    imageContentType?: string;
    removeImage?: boolean;
}

/**
 * Equipment catalogue CRUD for admins (backend rest/fyes/admin/equipment).
 * Reads go through the public EquipmentService; this is the write side, and it
 * is admin-only on the server (fyesAdminRequired).
 */
@Injectable({ providedIn: 'root' })
export class AdminEquipmentService extends HttpServiceBase {
    protected override baseUrl = '/admin/equipment';

    constructor(private http: HttpClient) {
        super();
        this.assign(this);
    }

    public list(): Observable<any> {
        return this._http.get(this.baseUrl)
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    public create(payload: EquipmentPayload): Observable<any> {
        return this._http.post(this.baseUrl, payload)
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    public update(id: string, payload: EquipmentPayload): Observable<any> {
        return this._http.put(`${this.baseUrl}/${id}`, payload)
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    public delete(id: string): Observable<any> {
        return this._http.delete(`${this.baseUrl}/${id}`)
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }
}
